import React, { useState, useMemo, useEffect } from 'react';
import { Box, Button, Switch, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormBuilder } from '../../utils/FormBuilder/FormBuilder';
import { generateValidationSchema } from '../../utils/FormBuilder/Validators';
import { FormField } from '../../interfaces/User.interface';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFetch } from '../../utils/FetchTemplate';
import { useAuth } from '../../contexts/AuthContext';
import PopupConfirm from '../../components/PopupConfirm';
import { useLoading } from '../../contexts/LoadingContext';

interface FormValues {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}

const Account: React.FC = () => {
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const { postDataResponse } = useFetch();
  const { user } = useAuth();
  const [ msg, setMsg ] = useState('');

  const handleToggle2FA = (checked: boolean) => {
    setIs2FAEnabled(checked);
    postDataResponse('/api/auth/toggle-2fa', { userId: user.userId, twoFactorCodeEnabled: checked }).then(res => {
      if (res.status === 200) {
        if (checked){
          setMsg('2FA enabled successfully');
        } else {
          setMsg('2FA disabled successfully');
        }
      } else {
        res.json().then(data => setMsg("Failed."));
        setIs2FAEnabled(!checked);
      }
    }).catch().finally();
  };

  useEffect(() => {
    setIs2FAEnabled(user.twoFactorCodeEnabled || false);
  }, [user]);

  const handleOpenChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  const handleCloseChangePassword = () => {
    setIsChangePasswordOpen(false);
  };

  const passwordFields: FormField[] = [
    {
      name: 'oldPassword',
      question: 'Old Password',
      validation: 'text',
      required: true,
      questionType: 'password',
    },
    {
      name: 'password',
      question: 'New Password',
      validation: 'password',
      required: true,
      questionType: 'password',
    },
    {
      name: 'confirmNewPassword',
      question: 'Confirm New Password',
      validation: 'confirmPassword',
      required: true,
      questionType: 'password',
    },
  ];

  const validationSchema = useMemo(() => generateValidationSchema<FormValues>(passwordFields), [passwordFields] );

  const { control, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
  });

  const formContent = FormBuilder(passwordFields, control, errors);

  const onSubmit = (data: FormValues) => {
    postDataResponse('/api/auth/change-password', { ...data, userId: user.userId }).then( res => {
      if (res.status === 200) {
        setMsg('Password changed successfully');
        handleCloseChangePassword();
      } else{
        return res.json().then(data => setMsg(data.message));
      }
    }).catch();
  };

  return (
    <Box>
      {msg && <PopupConfirm message={msg} onConfirm={() => setMsg('')} />}
      <Typography variant="h4" gutterBottom>
        Account Settings
      </Typography>

      <Box display="flex" alignItems="center" mb={2}>
        <Typography>Enable 2 Factor Authentication(the system will send a confirmation to your email to secure the login):</Typography>
        <Switch
          checked={is2FAEnabled}
          onChange={(event) => handleToggle2FA(event.target.checked)}
          color="primary"
        />
      </Box>

      <Button variant="contained" color="primary" onClick={handleOpenChangePassword}>
        Change Password
      </Button>

      <Dialog open={isChangePasswordOpen} onClose={handleCloseChangePassword}>
        <DialogTitle>Change Password</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {formContent}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseChangePassword}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Confirm
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Account;
